import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 + 402 responses
let lastUpgradeToastAt = 0;
api.interceptors.response.use(
  response => response,
  error => {
    // Diagnostyka: zapisz nieudane zapytania (4xx/5xx) dla auto-kontekstu zgłoszeń
    try {
      const st = error.response?.status;
      if (st && st >= 400) {
        const cfg = error.config || {};
        const detail = error.response?.data?.detail
          || error.response?.data?.message
          || error.message || '';
        import('./diagnostics').then(({ recordFailedRequest }) => {
          recordFailedRequest(cfg.method, (cfg.baseURL || '') + (cfg.url || ''), st, detail);
        }).catch(() => { /* silent */ });
      }
    } catch { /* never break request flow */ }

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    // 402 Payment Required — feature locked behind plan
    if (error.response?.status === 402) {
      // Throttle toasts (max 1 per 5s — żeby nie spamować przy multiple parallel requests)
      const now = Date.now();
      if (now - lastUpgradeToastAt > 5000) {
        lastUpgradeToastAt = now;
        // Lazy-import toast (sonner) — żeby nie wymagać przy auth flow
        import('sonner').then(({ toast }) => {
          const detail = error.response?.data?.detail || 'Funkcja wymaga upgrade planu';
          toast.error(detail, {
            duration: 8000,
            action: {
              label: 'Zobacz plany →',
              onClick: () => {
                window.location.href = '/plans';
              },
            },
          });
        }).catch(() => { /* silent */ });
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const loginUser = (data) => api.post('/auth/login', data).then(r => r.data);
export const registerUser = (data) => api.post('/auth/register', data).then(r => r.data);
export const getMe = () => api.get('/auth/me').then(r => r.data);
export const listUsers = () => api.get('/auth/users').then(r => r.data);
export const inviteUser = (data) => api.post('/auth/users/invite', data).then(r => r.data);
export const requestPasswordReset = (email) => api.post('/auth/password-reset/request', { email }).then(r => r.data);

// License
export const getLicense = () => api.get('/license').then(r => r.data);

// Email Config
export const getEmailConfig = () => api.get('/email-config').then(r => r.data);

// Data Export
export const exportData = async (type, format, tenantId) => {
  const response = await api.get(`/export/${type}`, {
    params: { format, tenant_id: tenantId },
    responseType: format === 'csv' ? 'text' : 'json'
  });
  return response.data;
};

// Tenant
export const listTenants = () => api.get('/tenants').then(r => r.data);
export const createTenant = (data) => api.post('/tenants', data).then(r => r.data);
export const getTenant = (id) => api.get(`/tenants/${id}`).then(r => r.data);

// Agent Config
export const getAgentConfig = (tenantId) => api.get('/agent-config', { params: { tenant_id: tenantId } }).then(r => r.data);
export const updateAgentConfig = (data, tenantId) => api.put('/agent-config', data, { params: { tenant_id: tenantId } }).then(r => r.data);

// Contractors — backend returns {contractors:[...], total, skip, limit}; unwrap to plain array.
// Pages that need total can call listContractorsRaw().
export const listContractorsRaw = (tenantId) =>
  api.get('/contractors', { params: { tenant_id: tenantId } }).then(r => r.data);
export const listContractors = (tenantId) =>
  listContractorsRaw(tenantId).then(d => Array.isArray(d) ? d : (d?.contractors || []));
export const createContractor = (data, tenantId) => api.post('/contractors', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getContractor = (id) => api.get(`/contractors/${id}`).then(r => r.data);
export const updateContractor = (id, data) => api.put(`/contractors/${id}`, data).then(r => r.data);
export const deleteContractor = (id) => api.delete(`/contractors/${id}`).then(r => r.data);
export const getContractorMemory = (id) => api.get(`/contractors/${id}/memory`).then(r => r.data);

// Negotiations
export const listNegotiations = (tenantId, contractorId = null) => api.get('/negotiations', { params: { tenant_id: tenantId, contractor_id: contractorId } }).then(r => r.data);
export const createNegotiation = (data, tenantId) => api.post('/negotiations', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getNegotiation = (id) => api.get(`/negotiations/${id}`).then(r => r.data);
export const sendNegotiationTurn = (id, data) => api.post(`/negotiations/${id}/turn`, data).then(r => r.data);

// Reports
export const generateReport = (negotiationId) => api.post(`/negotiations/${negotiationId}/report`).then(r => r.data);
export const listReports = (tenantId) => api.get('/reports', { params: { tenant_id: tenantId } }).then(r => r.data);
export const getReport = (id) => api.get(`/reports/${id}`).then(r => r.data);

// Email Drafts
export const createEmailDraft = (data, tenantId) => api.post('/email-drafts', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const listEmailDrafts = (tenantId) => api.get('/email-drafts', { params: { tenant_id: tenantId } }).then(r => r.data);
export const approveEmailDraft = (id, approved) => api.put(`/email-drafts/${id}/approve`, { approved }).then(r => r.data);
export const sendEmailDraft = (id) => api.post(`/email-drafts/${id}/send`).then(r => r.data);

// Phone Calls
export const createPhoneCall = (data, tenantId) => api.post('/phone-calls', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const listPhoneCalls = (tenantId) => api.get('/phone-calls', { params: { tenant_id: tenantId } }).then(r => r.data);
export const updatePhoneCall = (id, data) => api.put(`/phone-calls/${id}`, data).then(r => r.data);
// T22 2026-05-27 — POST /phone-calls/{id}/dial → Twilio call init
export const dialPhoneCall = (id) => api.post(`/phone-calls/${encodeURIComponent(id)}/dial`).then(r => r.data);
// T36 2026-05-29 — sprawdź czy Twilio skonfigurowany (gate dla Zadzwoń button)
export const getTwilioConfig = () => api.get('/twilio/config').then(r => r.data);

// Dashboard
export const getDashboardStats = (tenantId) => api.get('/dashboard/stats', { params: { tenant_id: tenantId } }).then(r => r.data);
export const getSalesPerformance = (tenantId, days = 30) => api.get('/dashboard/sales-performance', { params: { tenant_id: tenantId, days } }).then(r => r.data);

// Pipeline
export const getPipelineStages = (lang) => api.get('/pipeline/stages', { params: { lang } }).then(r => r.data);
export const getPipelineDeals = (tenantId) => api.get('/pipeline/deals', { params: { tenant_id: tenantId } }).then(r => r.data);
export const getPipelineSummary = (tenantId) => api.get('/pipeline/summary', { params: { tenant_id: tenantId } }).then(r => r.data);
export const createPipelineDeal = (data, tenantId) => api.post('/pipeline/deals', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const updatePipelineDeal = (dealId, data) => api.put(`/pipeline/deals/${dealId}`, data).then(r => r.data);

// Experience Events
export const addExperienceEvent = (contractorId, type, outcome, notes) =>
  api.post(`/contractors/${contractorId}/events`, null, { params: { event_type: type, outcome, notes } }).then(r => r.data);

// Contractor Research
export const researchContractors = (query, industry, region, tenantId) =>
  api.post('/contractors/research', null, { params: { query, industry, region, tenant_id: tenantId } }).then(r => r.data);
export const deepResearchContractor = (id) => api.post(`/contractors/${id}/deep-research`).then(r => r.data);
export const getResearchHistory = (tenantId) => api.get('/contractors/research/history', { params: { tenant_id: tenantId } }).then(r => r.data);

// Global Search
export const globalSearch = (q, tenantId) => api.get('/search', { params: { q, tenant_id: tenantId } }).then(r => r.data);

// Notifications
export const getNotifications = (tenantId) => api.get('/notifications', { params: { tenant_id: tenantId } }).then(r => r.data);
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`).then(r => r.data);

// Audit Logs
export const getAuditLogs = (tenantId) => api.get('/audit-logs', { params: { tenant_id: tenantId } }).then(r => r.data);

// Templates
export const listTemplates = (tenantId) => api.get('/templates', { params: { tenant_id: tenantId } }).then(r => r.data);
export const createTemplate = (name, brief, category, tenantId) =>
  api.post('/templates', null, { params: { name, brief_template: brief, category, tenant_id: tenantId } }).then(r => r.data);

// Password & Profile
export const changePassword = (oldPassword, newPassword) =>
  api.put('/auth/password', null, { params: { old_password: oldPassword, new_password: newPassword } }).then(r => r.data);
export const updateProfile = (data) =>
  api.put('/auth/profile', null, { params: data }).then(r => r.data);

// Products
export const listProducts = (tenantId, search = null, category = null, skip = 0, limit = 100, manufacturer = null) => api.get('/products', { params: { tenant_id: tenantId, search, category, skip, limit, manufacturer } }).then(r => r.data);
export const getManufacturersList = (tenantId) => api.get('/products/manufacturers', { params: { tenant_id: tenantId } }).then(r => r.data);
export const createProduct = (data, tenantId) => api.post('/products', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getProduct = (id) => api.get(`/products/${id}`).then(r => r.data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data).then(r => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then(r => r.data);
export const getCategoryTree = (tenantId) => api.get('/products/categories/tree', { params: { tenant_id: tenantId } }).then(r => r.data);
export const getSpecTemplates = () => api.get('/products/specs/templates').then(r => r.data);
export const importProductsCsv = (file, tenantId) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/products/import-csv', formData, {
    params: { tenant_id: tenantId },
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};
export const getCatalogSources = () => api.get('/lighting/catalog-sources').then(r => r.data);
// T43 2026-05-30 — import cennika dystrybutora → realny cost_price (zastępuje estymaty)
export const importPriceList = (file, { matchField = 'sku', marginPct = null } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = { match_field: matchField };
  if (marginPct != null) params.margin_pct = marginPct;
  return api.post('/products/import-price-list', formData, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then(r => r.data);
};

// Email Send
export const sendEmailDraftAction = (id) => api.post(`/email-drafts/${id}/send`).then(r => r.data);

// Offers (Kreator Oferty)
export const listOffers = (tenantId, contractorId, dealId, status) =>
  api.get('/offers', { params: { tenant_id: tenantId, contractor_id: contractorId, deal_id: dealId, status } }).then(r => r.data);
export const createOffer = (data, tenantId) => api.post('/offers', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getOffer = (id) => api.get(`/offers/${id}`).then(r => r.data);
export const updateOffer = (id, data) => api.put(`/offers/${id}`, data).then(r => r.data);
export const deleteOffer = (id) => api.delete(`/offers/${id}`).then(r => r.data);
export const generateOfferPdf = (id) => api.post(`/offers/${id}/generate-pdf`, null, { responseType: 'blob' }).then(r => r.data);
export const duplicateOffer = (id) => api.post(`/offers/${id}/duplicate`).then(r => r.data);

// Strategy Planner
export const generateStrategyPlan = (data, tenantId) => api.post('/strategy/generate', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const listStrategyPlans = (tenantId, negotiationId) => api.get('/strategy/plans', { params: { tenant_id: tenantId, negotiation_id: negotiationId } }).then(r => r.data);
export const getStrategyPlan = (id) => api.get(`/strategy/plans/${id}`).then(r => r.data);
export const deleteStrategyPlan = (id) => api.delete(`/strategy/plans/${id}`).then(r => r.data);

// Recommendations
export const getRecommendations = (tenantId) => api.get('/recommendations', { params: { tenant_id: tenantId } }).then(r => r.data);

// Project Analyzer
export const analyzeProject = (data, tenantId) => api.post('/projects/analyze', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const listProjectAnalyses = (tenantId) => api.get('/projects/analyses', { params: { tenant_id: tenantId } }).then(r => r.data);
export const getProjectAnalysis = (id) => api.get(`/projects/analyses/${id}`).then(r => r.data);
export const deleteProjectAnalysis = (id) => api.delete(`/projects/analyses/${id}`).then(r => r.data);
export const getLightingNorms = () => api.get('/projects/norms').then(r => r.data);
export const getLightingRecommendation = (payload, tenantId) =>
  api.post('/projects/lighting/recommend', payload, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getLightingReport = (recommendationId) =>
  api.get(`/projects/lighting/report/${recommendationId}`).then(r => r.data);
export const getLightingStf = (payload) =>
  api.post('/projects/lighting/stf', payload).then(r => r.data);
export const calculateRoom = (data) => api.post('/projects/calculate-room', data).then(r => r.data);
export const analyzeProjectDocument = (file, tenantId, context = '') => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/projects/analyze-document', formData, {
    params: { tenant_id: tenantId, context },
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  }).then(r => r.data);
};

// Lead Scoring
export const getLeadQueue = (tenantId, limit) => api.get('/leads/queue', { params: { tenant_id: tenantId, limit } }).then(r => r.data);
export const getLeadStats = (tenantId) => api.get('/leads/stats', { params: { tenant_id: tenantId } }).then(r => r.data);
export const getContractorLeadScore = (id) => api.get(`/leads/score/${id}`).then(r => r.data);

// Pricing Rules
export const getPricingRules = (tenantId) => api.get('/pricing-rules', { params: { tenant_id: tenantId } }).then(r => r.data);
export const updatePricingRules = (data, tenantId) => api.put('/pricing-rules', data, { params: { tenant_id: tenantId } }).then(r => r.data);

// ICP Config
export const getIcpConfig = (tenantId) => api.get('/icp-config', { params: { tenant_id: tenantId } }).then(r => r.data);
export const updateIcpConfig = (data, tenantId) => api.put('/icp-config', data, { params: { tenant_id: tenantId } }).then(r => r.data);

// Agent Tasks
export const runTaskManually = (taskId, tenantId) => api.post(`/tasks/${taskId}/run`, null, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getTaskHistory = (tenantId) => api.get('/tasks/history', { params: { tenant_id: tenantId } }).then(r => r.data);

// Active Company Search
export const searchCompanies = (data, tenantId) => api.post('/company-search', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const searchCompaniesAuto = (tenantId) => api.post('/company-search/auto', {}, { params: { tenant_id: tenantId } }).then(r => r.data);
export const importSearchResult = (company, tenantId) => api.post('/company-search/import', company, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getSearchHistory = (tenantId) => api.get('/company-search/history', { params: { tenant_id: tenantId } }).then(r => r.data);

// Investment Matching
export const runInvestmentMatch = (tenantId) => api.post('/investment-match', {}, { params: { tenant_id: tenantId } }).then(r => r.data);
export const executeInvestmentMatch = (match, tenantId) => api.post('/investment-match/execute', match, { params: { tenant_id: tenantId } }).then(r => r.data);
export const getInvestmentMatchHistory = (tenantId) => api.get('/investment-match/history', { params: { tenant_id: tenantId } }).then(r => r.data);

// Architect Search & Matching
export const searchArchitects = (data, tenantId) => api.post('/architects/search', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const matchArchitects = (tenantId) => api.post('/architects/match', {}, { params: { tenant_id: tenantId } }).then(r => r.data);

// Investor Search & Matching
export const searchInvestors = (data, tenantId) => api.post('/investors/search', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const matchInvestors = (tenantId) => api.post('/investors/match', {}, { params: { tenant_id: tenantId } }).then(r => r.data);

// ===== COMPETITIVE INTELLIGENCE =====
export const addCompetitor = (data) => api.post('/intelligence/competitors', data).then(r => r.data);
export const listCompetitors = () => api.get('/intelligence/competitors').then(r => r.data);
export const scanCompetitor = (id) => api.post(`/intelligence/competitors/${id}/scan`).then(r => r.data);
export const getCompetitorHistory = (id, limit = 20) => api.get(`/intelligence/competitors/${id}/history`, { params: { limit } }).then(r => r.data);
export const getCompetitiveLandscape = () => api.post('/intelligence/landscape').then(r => r.data);
export const getCompetitiveResponse = (competitorId, change) => api.post(`/intelligence/competitors/${competitorId}/response`, { change }).then(r => r.data);

// ===== FINANCIAL HEALTH SCORING =====
export const scoreCompanyFinancial = (data) => api.post('/intelligence/financial-score', data).then(r => r.data);
export const getContractorFinancialScore = (id) => api.get(`/intelligence/financial-score/${id}`).then(r => r.data);
export const generateFinancialReport = (id) => api.post(`/intelligence/financial-report/${id}`).then(r => r.data);

// ===== GHOST INTELLIGENCE =====
export const getGhostDashboard = () => api.get('/intelligence/ghosts').then(r => r.data);
export const runAllGhosts = () => api.post('/intelligence/ghosts/run').then(r => r.data);
export const getGhostSignals = (params) => api.get('/intelligence/ghosts/signals', { params }).then(r => r.data);
export const submitGhostFeedback = (signalId, data) => api.post(`/intelligence/ghosts/signals/${signalId}/feedback`, data).then(r => r.data);
export const getGhostAnalytics = () => api.get('/intelligence/ghosts/analytics').then(r => r.data);
export const getGhostPatterns = (ghostName) => api.get('/intelligence/ghosts/patterns', { params: { ghost_name: ghostName } }).then(r => r.data);
export const getGhostWeights = () => api.get('/intelligence/ghosts/weights').then(r => r.data);

// ===== GEO-TARGETING =====
export const findInvestmentsNear = (data) => api.post('/intelligence/geo/investments-near', data).then(r => r.data);
export const findCompaniesNear = (data) => api.post('/intelligence/geo/companies-near', data).then(r => r.data);
export const getGeoReport = (data) => api.post('/intelligence/geo/report', data).then(r => r.data);

// ===== PREDICTIVE DEAL SCORING =====
export const getDealScore = (dealId) => api.get(`/intelligence/deal-score/${dealId}`).then(r => r.data);
export const getAllDealScores = () => api.get('/intelligence/deal-scores').then(r => r.data);
export const getPipelineForecast = (months = 3) => api.get('/intelligence/pipeline-forecast', { params: { months } }).then(r => r.data);
export const getDealTips = (dealId) => api.post(`/intelligence/deal-score/${dealId}/tips`).then(r => r.data);
export const trainPredictiveModel = () => api.post('/intelligence/train-model').then(r => r.data);

// ===== A/B EMAIL TESTING =====
export const createABTest = (data) => api.post('/intelligence/ab-test', data).then(r => r.data);
export const listABTests = (status) => api.get('/intelligence/ab-tests', { params: { status } }).then(r => r.data);
export const generateABVariants = (testId, data) => api.post(`/intelligence/ab-test/${testId}/generate-variants`, data).then(r => r.data);
export const sendABTest = (testId) => api.post(`/intelligence/ab-test/${testId}/send`).then(r => r.data);
export const getABTestResults = (testId) => api.get(`/intelligence/ab-test/${testId}/results`).then(r => r.data);

// ===== PROPOSALS =====
export const generateProposal = (data) => api.post('/proposals/generate', data).then(r => r.data);
export const proposalFromNegotiation = (negId) => api.post(`/proposals/from-negotiation/${negId}`).then(r => r.data);
export const listProposals = (contractorId) => api.get('/proposals', { params: { contractor_id: contractorId } }).then(r => r.data);
export const getProposal = (id) => api.get(`/proposals/${id}`).then(r => r.data);
export const getProposalPdf = (id) => api.get(`/proposals/${id}/pdf`, { responseType: 'blob' }).then(r => r.data);
export const listProposalTemplates = () => api.get('/proposal-templates').then(r => r.data);

// ===== CONTRACTS =====
export const generateContract = (data) => api.post('/contracts/generate', data).then(r => r.data);
export const listContracts = (contractorId) => api.get('/contracts', { params: { contractor_id: contractorId } }).then(r => r.data);
export const getContract = (id) => api.get(`/contracts/${id}`).then(r => r.data);
export const getContractPdf = (id) => api.get(`/contracts/${id}/pdf`, { responseType: 'blob' }).then(r => r.data);
export const listContractTemplates = () => api.get('/contract-templates').then(r => r.data);
export const createContractTemplate = (data) => api.post('/contract-templates', data).then(r => r.data);

// ===== MEETINGS =====
export const listMeetings = (days = 30) => api.get('/meetings', { params: { days } }).then(r => r.data);
export const createMeeting = (data) => api.post('/meetings', data).then(r => r.data);
export const proposeMeetingTimes = (data) => api.post('/meetings/propose', data).then(r => r.data);
export const getAvailableSlots = (dateFrom, dateTo, duration) => api.get('/meetings/available-slots', { params: { date_from: dateFrom, date_to: dateTo, duration } }).then(r => r.data);
export const cancelMeeting = (id, reason) => api.delete(`/meetings/${id}`, { params: { reason } }).then(r => r.data);
export const rescheduleMeeting = (id, data) => api.put(`/meetings/${id}/reschedule`, data).then(r => r.data);
export const getMeetingSummary = (id, notes) => api.post(`/meetings/${id}/summary`, { notes }).then(r => r.data);

// ===== WHATSAPP =====
export const sendWhatsApp = (data) => api.post('/whatsapp/send', data).then(r => r.data);
export const sendWhatsAppTemplate = (data) => api.post('/whatsapp/send-template', data).then(r => r.data);
export const getWhatsAppHistory = (contractorId) => api.get(`/whatsapp/history/${contractorId}`).then(r => r.data);

// ===== GENOME =====
export const getGenomeHealth = (tenantId) => api.get('/genome', { params: { tenant_id: tenantId } }).then(r => r.data);
export const initGenome = (params) => api.post('/genome/init', null, { params }).then(r => r.data);
export const updateGenomeProducts = (data, tenantId) => api.put('/genome/products', data, { params: { tenant_id: tenantId } }).then(r => r.data);
export const toggleGenomeCollective = (tenantId, optIn) => api.post('/genome/collective', null, { params: { tenant_id: tenantId, opt_in: optIn } }).then(r => r.data);
export const getGenomeContext = (tenantId, archetype) => api.get('/genome/context', { params: { tenant_id: tenantId, archetype } }).then(r => r.data);

// ===== CRM SYNC =====
export const fullCrmSync = () => api.post('/crm/sync').then(r => r.data);
export const pushToCrm = (entity) => api.post('/crm/sync/push', { entity }).then(r => r.data);
export const pullFromCrm = (entity) => api.post('/crm/sync/pull', { entity }).then(r => r.data);
export const getCrmSyncStatus = () => api.get('/crm/sync/status').then(r => r.data);

// ===== MANUFACTURERS / CATALOGS =====
export const listManufacturers = (tenantId) => api.get('/catalogs/manufacturers', { params: { tenant_id: tenantId } }).then(r => r.data);
export const addManufacturer = (data) => api.post('/catalogs/manufacturers', data).then(r => r.data);
export const deleteManufacturer = (id) => api.delete(`/catalogs/manufacturers/${id}`).then(r => r.data);
export const updateManufacturer = (id, data) => api.put(`/catalogs/manufacturers/${id}`, data).then(r => r.data);
export const discoverCatalog = (mfrId, tenantId) => api.post(`/catalogs/manufacturers/${mfrId}/discover`, { tenant_id: tenantId }).then(r => r.data);
export const scrapeCatalog = (mfrId, tenantId) => api.post(`/catalogs/manufacturers/${mfrId}/scrape`, { tenant_id: tenantId }).then(r => r.data);
export const importCatalogCsv = (mfrId, file, tenantId) => { const fd = new FormData(); fd.append('file', file); return api.post(`/catalogs/manufacturers/${mfrId}/import-csv?tenant_id=${tenantId}`, fd).then(r => r.data); };
export const importCatalogPdf = (mfrId, file, tenantId) => { const fd = new FormData(); fd.append('file', file); return api.post(`/catalogs/manufacturers/${mfrId}/import-pdf?tenant_id=${tenantId}`, fd).then(r => r.data); };
export const importCatalogLdt = (mfrId, file, tenantId) => { const fd = new FormData(); fd.append('file', file); return api.post(`/catalogs/manufacturers/${mfrId}/import-ldt?tenant_id=${tenantId}`, fd).then(r => r.data); };
export const updateCatalog = (mfrId, data) => api.put(`/catalogs/manufacturers/${mfrId}`, data).then(r => r.data);
export const getManufacturerProducts = (mfrId, params) => api.get(`/catalogs/manufacturers/${mfrId}/products`, { params }).then(r => r.data);
export const getManufacturerHistory = (mfrId) => api.get(`/catalogs/manufacturers/${mfrId}/history`).then(r => r.data);
export const importLocalCatalogs = (data) => api.post('/catalogs/import-local', data).then(r => r.data);

// ===== DIALUX =====
export const createDialuxTask = (data) => api.post('/dialux/tasks', data).then(r => r.data);
export const getDialuxTask = (id) => api.get(`/dialux/tasks/${id}`).then(r => r.data);
export const listDialuxTasks = (params) => api.get('/dialux/tasks', { params }).then(r => r.data);
export const createDialuxComparison = (data) => api.post('/dialux/comparisons', data).then(r => r.data);
export const getDialuxComparison = (id) => api.get(`/dialux/comparisons/${id}`).then(r => r.data);
export const getDialuxAgentStatus = () => api.get('/dialux/agent/status').then(r => r.data);

// Opportunities — Okazje biznesowe
export const listOpportunities = (params) => api.get('/intelligence/opportunities', { params }).then(r => r.data);
export const getOpportunityStats = (params) => api.get('/intelligence/opportunities/stats', { params }).then(r => r.data);
export const searchOpportunitiesNow = (params) => api.post('/intelligence/opportunities/search', null, { params }).then(r => r.data);
// Most SPHINX → MASKIL: ręczny pull przetargów (T71)
export const pullSphinxTenders = (params) => api.post('/tenders/sphinx-pull', null, { params, timeout: 120000 }).then(r => r.data);
// Video Studio (T77) — blueprint wideo sprzedażowego z przetargu/inwestycji
export const createVideoBlueprint = (body) => api.post('/tenders/video-blueprint', body, { timeout: 120000 }).then(r => r.data);
export const listVideoBlueprints = (limit = 30) => api.get('/tenders/video-blueprints', { params: { limit } }).then(r => r.data);
export const getVideoBlueprint = (id) => api.get(`/tenders/video-blueprint/${encodeURIComponent(id)}`).then(r => r.data);

// ===== TENDERS (SWP24-killer) =====
export const listTenders = (params) => api.get('/tenders', { params }).then(r => r.data);
export const listTenderIndustries = () => api.get('/tenders/industries').then(r => r.data);
export const generateOfferFromTender = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/generate-offer`).then(r => r.data);

// MASKIL Agent — Wygeneruj odpowiedź na SWZ (smart-tier opus-4-7 dla contract role)
// 2026-05-26: pierwszy UI hook agenta (Ścieżka D z audytu)
export const generateSwzResponse = (id, body = {}) =>
  api.post(`/maskil-agent/swz-response/${encodeURIComponent(id)}`, body).then(r => r.data);

// ===== Interior Designers (T17 2026-05-27) =====
export const listInteriorDesigners = (params = {}) =>
  api.get('/interior-designers', { params }).then(r => r.data);
export const getInteriorDesignerStats = () =>
  api.get('/interior-designers/stats').then(r => r.data);
export const createInteriorDesigner = (body) =>
  api.post('/interior-designers', body).then(r => r.data);
export const getInteriorDesigner = (id) =>
  api.get(`/interior-designers/${encodeURIComponent(id)}`).then(r => r.data);
export const updateInteriorDesigner = (id, body) =>
  api.patch(`/interior-designers/${encodeURIComponent(id)}`, body).then(r => r.data);
export const deleteInteriorDesigner = (id) =>
  api.delete(`/interior-designers/${encodeURIComponent(id)}`).then(r => r.data);
export const linkDesignerToInvestment = (designerId, investmentId) =>
  api.post(`/interior-designers/${encodeURIComponent(designerId)}/link-investment`, null,
    { params: { investment_id: investmentId } }).then(r => r.data);
// T35 2026-05-29 (mock-cleanup): triggerInteriorImport/triggerHouzzImport/
// triggerSeedImport USUNIĘTE — endpointy /import/run, /import/houzz,
// /import/seed-known-studios skasowane (fake/martwe źródła). Discovery
// wyłącznie przez discover-from-sphinx (Serper Google) poniżej.
export const sphinxEnrichDesigner = (id, depth = 'full') =>
  api.post(`/interior-designers/${encodeURIComponent(id)}/sphinx-enrich`, null,
    { params: { depth } }).then(r => r.data);
// T40 2026-05-29 — contact extraction ze strony www (MASKIL sam scrapuje /kontakt)
export const extractDesignerContacts = (id) =>
  api.post(`/interior-designers/${encodeURIComponent(id)}/extract-contacts`).then(r => r.data);
export const bulkExtractContacts = (limit = 50, onlyMissing = true) =>
  api.post('/interior-designers/extract-contacts', null,
    { params: { limit, only_missing: onlyMissing } }).then(r => r.data);

// ===== Telegram bot (T32 2026-05-29) =====
export const getTelegramStatus = () => api.get('/telegram/me/status').then(r => r.data);
export const generateTelegramPairingLink = () => api.post('/telegram/me/pairing-link').then(r => r.data);
export const unlinkTelegram = () => api.post('/telegram/me/unlink').then(r => r.data);
export const testTelegramNotify = (title, body) =>
  api.post('/telegram/admin/test-notify-me', { title, body }).then(r => r.data);
export const adminSetupTelegramWebhook = () => api.post('/telegram/admin/setup').then(r => r.data);
export const getTelegramAdminStatus = () => api.get('/telegram/admin/status').then(r => r.data);

// Persona Wizard — T4 2026-05-27 (P1 follow-up dla Izy, persona hybrid)
export const setPersona = (persona) =>
  api.patch('/auth/preferences/persona', { persona }).then(r => r.data);
export const triggerAutoFetchSwz = (params = {}) =>
  api.post(`/tenders/auto-fetch-swz`, null, { params }).then(r => r.data);
// Q&A — pytania i odpowiedzi zamawiającego (D2a)
export const listTenderQa = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/qa`).then(r => r.data);
export const addTenderQa = (id, body) =>
  api.post(`/tenders/${encodeURIComponent(id)}/qa`, body).then(r => r.data);
export const updateTenderQa = (id, qaId, body) =>
  api.put(`/tenders/${encodeURIComponent(id)}/qa/${qaId}`, body).then(r => r.data);
export const deleteTenderQa = (id, qaId) =>
  api.delete(`/tenders/${encodeURIComponent(id)}/qa/${qaId}`).then(r => r.data);
// D2b — auto-scrape Q&A z BZP via Playwright (long-running 10-30s)
export const autoScrapeTenderQa = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/qa/auto-scrape`, null,
    { timeout: 60000 }).then(r => r.data);

// Per-tender override — 🔥 priorytet / 🔇 nieistotne (2026-05-06)
export const setTenderOverride = (id, body) =>
  api.post(`/tenders/${encodeURIComponent(id)}/override`, body).then(r => r.data);
export const removeTenderOverride = (id) =>
  api.delete(`/tenders/${encodeURIComponent(id)}/override`).then(r => r.data);
export const getTenderOverride = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/override`).then(r => r.data);

// Scoring profile — user może edytować custom keywords/CPV/exclude (2026-05-06)
export const getScoringProfile = () =>
  api.get('/tenders/scoring-profile').then(r => r.data);
export const updateScoringProfile = (body) =>
  api.put('/tenders/scoring-profile', body).then(r => r.data);

// F1: Decision chain — łańcuch decyzyjny per tender (Kompas Inwestycji-style)
export const getDecisionChain = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/decision-chain`).then(r => r.data);
export const triggerChainResearch = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/decision-chain/research`).then(r => r.data);
export const updateDecisionChain = (id, body) =>
  api.put(`/tenders/${encodeURIComponent(id)}/decision-chain`, body).then(r => r.data);
// T33 2026-05-29: SPHINX enrich per-role w decision chain
export const sphinxEnrichChainRole = (tenderId, role, depth = 'full') =>
  api.post(`/tenders/${encodeURIComponent(tenderId)}/decision-chain/sphinx-enrich-role`, null,
    { params: { role, depth } }).then(r => r.data);

// F4: Timeline cross-reference (GUNB ↔ plan ↔ tender ↔ award)
export const getTenderTimeline = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/timeline`).then(r => r.data);

// G1: Geocoding
export const geocodeBackfill = (limit = 50) =>
  api.post('/intelligence/geocode/backfill', null, { params: { limit } }).then(r => r.data);
export const getGeocodeStats = () =>
  api.get('/intelligence/geocode/stats').then(r => r.data);

// B1: Architects tracking
export const listArchitects = (params = {}) =>
  api.get('/intelligence/architects', { params }).then(r => r.data);
export const getArchitectDetail = (licenseNumber) =>
  api.get(`/intelligence/architects/${encodeURIComponent(licenseNumber)}`).then(r => r.data);
export const getArchitectsStats = () =>
  api.get('/intelligence/architects/stats').then(r => r.data);
export const triggerGunbScan = (voivodeship, max_leads = 30) =>
  api.post('/intelligence/architects/scan-gunb', null, {
    params: { voivodeship, max_leads },
    timeout: 600000,  // 10 min — full scan może trwać
  }).then(r => r.data);
// Bulk enrichment kontaktów architektów (priorytet wg liczby inwestycji) — T74
export const enrichArchitectsContacts = (limit = 20) =>
  api.post('/intelligence/architects/enrich-contacts', null, {
    params: { limit },
    timeout: 300000,
  }).then(r => r.data);
export const searchTenders = (body) => api.post('/tenders/search', body).then(r => r.data);
export const getTenderStats = () => api.get('/tenders/stats').then(r => r.data);
export const getTenderDetail = (id) => api.get(`/tenders/${encodeURIComponent(id)}`).then(r => r.data);
export const listTenderAttachments = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/attachments`).then(r => r.data);
export const triggerTenderDownload = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/download`).then(r => r.data);
export const analyzeTender = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/analyze`).then(r => r.data);
export const runTenderConsilium = (id, analysisType = 'red_team') =>
  api.post(`/tenders/${encodeURIComponent(id)}/consilium`, null, {
    params: { analysis_type: analysisType },
    timeout: 240000,
  }).then(r => r.data);
export const listConsiliumTypes = () => api.get('/tenders/consilium/types').then(r => r.data);
export const deepSearchTenders = (body) => api.post('/tenders/deep-search', body).then(r => r.data);
export const getVectorIndexStats = () => api.get('/tenders/index/stats').then(r => r.data);
export const addToTenderWatchlist = (body) => api.post('/tenders/watch', body).then(r => r.data);
export const removeFromTenderWatchlist = (id) =>
  api.delete(`/tenders/watch/${encodeURIComponent(id)}`).then(r => r.data);
export const getTenderWatchlist = () => api.get('/tenders/watchlist').then(r => r.data);
export const getTenderAttachmentPdfUrl = (oppId, attId) =>
  `${API_BASE}/tenders/${encodeURIComponent(oppId)}/pdf/${encodeURIComponent(attId)}`;

// ===== Red Flags, Outreach, JEDZ, Win Rate, Similar =====
export const getTenderRedFlags = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/red-flags`).then(r => r.data);
export const getTenderWinRate = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/win-rate`).then(r => r.data);
export const getSimilarTenders = (id, topK = 10) =>
  api.get(`/tenders/${encodeURIComponent(id)}/similar`, { params: { top_k: topK } }).then(r => r.data);
export const generateOutreach = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/outreach/generate`, null, { timeout: 120000 }).then(r => r.data);
export const listOutreachDrafts = (id) =>
  api.get(`/tenders/${encodeURIComponent(id)}/outreach/drafts`).then(r => r.data);
export const sendOutreachDraft = (draftId) =>
  api.post(`/tenders/outreach/send/${draftId}`, null, { timeout: 60000 }).then(r => r.data);
export const generateJedzDraft = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/jedz/generate`, null, { timeout: 60000 }).then(r => r.data);
export const getTenderChanges = (id, includeQuiet = false) =>
  api.get(`/tenders/${encodeURIComponent(id)}/changes`, { params: { include_quiet: includeQuiet } }).then(r => r.data);
export const getChangeFullDiff = (changeEventId) =>
  api.get(`/tenders/changes/${changeEventId}/diff`).then(r => r.data);
export const acknowledgeChange = (changeEventId) =>
  api.post(`/tenders/changes/${changeEventId}/acknowledge`).then(r => r.data);
export const triggerSwzChangeCheck = (id) =>
  api.post(`/tenders/${encodeURIComponent(id)}/check-changes`, null, { timeout: 90000 }).then(r => r.data);
export const getJedzFileUrl = (id, filename) =>
  `${API_BASE}/tenders/${encodeURIComponent(id)}/jedz/file/${encodeURIComponent(filename)}`;

// ===== PRE-TENDER INTELLIGENCE =====
export const listPretenderSignals = (params) =>
  api.get('/pretender/signals', { params }).then(r => r.data);
export const listPretenderSources = () =>
  api.get('/pretender/signals/sources').then(r => r.data);
export const collectPretenderSignals = (params) =>
  api.post('/pretender/signals/collect', null, { params, timeout: 120000 }).then(r => r.data);
export const listPretenderCandidates = (params) =>
  api.get('/pretender/candidates', { params }).then(r => r.data);
export const getPretenderCandidate = (id) =>
  api.get(`/pretender/candidates/${encodeURIComponent(id)}`).then(r => r.data);
export const getPretenderStats = () =>
  api.get('/pretender/stats').then(r => r.data);

// ===== SUBSCRIPTIONS (Faza 9.3) =====
export const listSubscriptions = (params = {}) =>
  api.get('/subscriptions', { params }).then(r => r.data);
export const createSubscription = (body) =>
  api.post('/subscriptions', body).then(r => r.data);
export const updateSubscription = (id, body) =>
  api.put(`/subscriptions/${encodeURIComponent(id)}`, body).then(r => r.data);
export const deleteSubscription = (id) =>
  api.delete(`/subscriptions/${encodeURIComponent(id)}`).then(r => r.data);
export const toggleSubscription = (id) =>
  api.post(`/subscriptions/toggle/${encodeURIComponent(id)}`).then(r => r.data);

export const followTender = (tenderId) =>
  api.post(`/subscriptions/follow/tender/${encodeURIComponent(tenderId)}`).then(r => r.data);
export const followRegion = (region) =>
  api.post('/subscriptions/follow/region', { region }).then(r => r.data);
export const followCpv = (cpv) =>
  api.post('/subscriptions/follow/cpv', { cpv }).then(r => r.data);
export const followEntity = (nip, name) =>
  api.post('/subscriptions/follow/entity', { nip, name }).then(r => r.data);
export const followKeyword = (keyword) =>
  api.post('/subscriptions/follow/keyword', { keyword }).then(r => r.data);

export const listSubscriptionDeliveries = (params = {}) =>
  api.get('/subscriptions/deliveries', { params }).then(r => r.data);

// In-app notifications (bell icon)
export const listNotifications = (params = {}) =>
  api.get('/inapp', { params }).then(r => r.data);
export const markNotificationsRead = (ids = [], all = false) =>
  api.post('/inapp/mark-read', { ids, all }).then(r => r.data);

// ===== FEED (Faza 9.2) =====
export const listFeed = (params) => api.get('/feed', { params }).then(r => r.data);
export const getFeedStats = (params) => api.get('/feed/stats', { params }).then(r => r.data);
export const markFeedRead = (item_ids) => api.post('/feed/mark-read', { item_ids }).then(r => r.data);
export const markAllFeedRead = (category) =>
  api.post('/feed/mark-all-read', null, { params: category ? { category } : {} }).then(r => r.data);
export const listFeedCategories = () => api.get('/feed/categories').then(r => r.data);

// WebSocket events (Faza 9.1b)
export const getEventsWebSocketUrl = () => {
  const token = localStorage.getItem('token') || '';
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = window.location.host;
  return `${scheme}://${host}/api/ws/events?token=${encodeURIComponent(token)}`;
};

// =====================================================
// MASKIL Pre-Bid Agent — Pricing & RFQ (Phases 1, 3-5, 7, 12, PROF-3, PROF-4)
// =====================================================

// Suppliers Knowledge Base
export const listSuppliers = (params = {}) =>
  api.get('/suppliers', { params }).then(r => r.data);
export const getSupplierStats = () =>
  api.get('/suppliers/stats').then(r => r.data);
export const seedSuppliers = (enrich_with_krs = false) =>
  api.post('/suppliers/seed', null, { params: { enrich_with_krs } }).then(r => r.data);
export const getSupplier = (supplier_id) =>
  api.get(`/suppliers/${supplier_id}`).then(r => r.data);
export const createSupplier = (data) =>
  api.post('/suppliers', data).then(r => r.data);
export const updateSupplier = (supplier_id, data) =>
  api.patch(`/suppliers/${supplier_id}`, data).then(r => r.data);
export const deactivateSupplier = (supplier_id) =>
  api.delete(`/suppliers/${supplier_id}`).then(r => r.data);

// Award outcomes
export const setTenderOutcome = (opp_id, data) =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/outcome`, data).then(r => r.data);
export const getOutcomesSummary = () =>
  api.get('/tenders/outcomes/summary').then(r => r.data);

// Mark submitted
export const markTenderSubmitted = (opp_id, data = {}) =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/submit`, data).then(r => r.data);

// Email config status
export const getEmailStatus = () =>
  api.get('/email/status').then(r => r.data);

// SPHINX Intelligence
export const intelStatus = () =>
  api.get('/intel/status').then(r => r.data);
export const intelCoverage = () =>
  api.get('/intel/coverage').then(r => r.data);
export const intelCompanyQuick = (data) =>
  api.post('/intel/company/quick', data).then(r => r.data);
export const intelCompanyFull = (data) =>
  api.post('/intel/company/full', data).then(r => r.data);
export const intelCompanyDeep = (data) =>
  api.post('/intel/company/deep', data).then(r => r.data);
export const intelAmlCheck = (data) =>
  api.post('/intel/aml-check', data).then(r => r.data);
export const enrichTenderAuthority = (opp_id) =>
  api.post(`/intel/tenders/${encodeURIComponent(opp_id)}/authority-intel`).then(r => r.data);
export const enrichContractor = (contractor_id) =>
  api.post(`/intel/contractors/${contractor_id}/enrich`).then(r => r.data);
export const enrichSupplier = (supplier_id) =>
  api.post(`/intel/suppliers/${supplier_id}/enrich`).then(r => r.data);

// Supplier contact discovery (Etap A, 2026-05-13)
export const discoverSupplierContact = (supplier_id, opts = {}) =>
  api.post(`/suppliers/${supplier_id}/discover-contact`, null, {
    params: opts,
    timeout: 60000,
  }).then(r => r.data);

// Per-tenant supplier contacts ("Moi opiekunowie") — Etap B
export const listSupplierContacts = (supplier_id) =>
  api.get(`/suppliers/${supplier_id}/contacts`).then(r => r.data);
export const createSupplierContact = (supplier_id, body) =>
  api.post(`/suppliers/${supplier_id}/contacts`, body).then(r => r.data);
export const updateSupplierContact = (supplier_id, contact_id, body) =>
  api.patch(`/suppliers/${supplier_id}/contacts/${contact_id}`, body).then(r => r.data);
export const deleteSupplierContact = (supplier_id, contact_id) =>
  api.delete(`/suppliers/${supplier_id}/contacts/${contact_id}`).then(r => r.data);

// ===== INVESTMENTS (Etap 2, 2026-05-14) — Pipeline B =====
export const listInvestments = (params = {}) =>
  api.get('/investments', { params }).then(r => r.data);
export const getInvestmentStats = () =>
  api.get('/investments/stats').then(r => r.data);
// T67 — per-user scoring profile inwestycji
export const getMyInvScoringProfile = () =>
  api.get('/investments/scoring-profile/me').then(r => r.data);
export const putMyInvScoringProfile = (body) =>
  api.put('/investments/scoring-profile/me', body).then(r => r.data);
export const getInvestment = (id) =>
  api.get(`/investments/${encodeURIComponent(id)}`).then(r => r.data);
export const createInvestment = (body) =>
  api.post('/investments', body).then(r => r.data);
export const updateInvestment = (id, body) =>
  api.patch(`/investments/${encodeURIComponent(id)}`, body).then(r => r.data);
export const changeInvestmentStage = (id, stage, note) =>
  api.post(`/investments/${encodeURIComponent(id)}/stage`, { stage, note }).then(r => r.data);
export const logSpecIn = (id, body) =>
  api.post(`/investments/${encodeURIComponent(id)}/spec-in`, body).then(r => r.data);
export const removeSpecIn = (id, product_id) =>
  api.delete(`/investments/${encodeURIComponent(id)}/spec-in/${product_id}`).then(r => r.data);
export const listOutreach = (id) =>
  api.get(`/investments/${encodeURIComponent(id)}/outreach`).then(r => r.data);
export const logOutreach = (id, body) =>
  api.post(`/investments/${encodeURIComponent(id)}/outreach`, body).then(r => r.data);
export const discoverFromGunb = (body) =>
  api.post('/investments/discover-from-gunb', body, { timeout: 90000 }).then(r => r.data);
export const exportInvestmentsXlsx = (params = {}) =>
  api.get('/investments/export', {
    params,
    responseType: 'blob',
    timeout: 120000,
  }).then(r => {
    const blob = new Blob([r.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    // Wyciągnij filename z Content-Disposition (fallback z timestamp)
    let filename = 'maskil_inwestycje.xlsx';
    const cd = r.headers && (r.headers['content-disposition'] || r.headers['Content-Disposition']);
    if (cd) {
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m) filename = m[1];
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { ok: true, filename };
  });
export const backfillConstructionPhase = (params = {}) =>
  api.post('/investments/backfill-construction-phase', null, { params, timeout: 120000 }).then(r => r.data);
export const findLinkedTender = (id) =>
  api.get(`/investments/${encodeURIComponent(id)}/linked-tender`).then(r => r.data);
export const linkInvestmentTender = (id, tender_source_id) =>
  api.post(`/investments/${encodeURIComponent(id)}/link-tender`, { tender_source_id }).then(r => r.data);

// ===== USER REPORTS (zgłaszanie problemów, V2 2026-05-18) =====
export const submitUserReport = (data) =>
  api.post('/user-reports/submit', data).then(r => r.data);

export const attachToUserReport = (reportId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(
    `/user-reports/${encodeURIComponent(reportId)}/attach`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 },
  ).then(r => r.data);
};

export const downloadReportAttachment = (reportId, filename) =>
  `${API_BASE}/user-reports/admin/${encodeURIComponent(reportId)}/attachment/${encodeURIComponent(filename)}`;

// ===== FILTER PRESETS (2026-05-26 — UX inspired by Kompas) =====
export const listFilterPresets = (scope) =>
  api.get('/filter-presets', { params: scope ? { scope } : {} }).then(r => r.data);
export const createFilterPreset = (name, scope, filters) =>
  api.post('/filter-presets', { name, scope, filters }).then(r => r.data);
export const updateFilterPreset = (id, body) =>
  api.patch(`/filter-presets/${encodeURIComponent(id)}`, body).then(r => r.data);
export const deleteFilterPreset = (id) =>
  api.delete(`/filter-presets/${encodeURIComponent(id)}`).then(r => r.data);
export const getMyUserReports = (limit = 50) =>
  api.get('/user-reports/my', { params: { limit } }).then(r => r.data);
export const getMyUserReportDetail = (id) =>
  api.get(`/user-reports/my/${id}`).then(r => r.data);
export const addMyUserReportComment = (id, text) =>
  api.post(`/user-reports/my/${id}/comment`, { text }).then(r => r.data);
export const confirmMyUserReportFix = (id) =>
  api.post(`/user-reports/my/${id}/confirm`).then(r => r.data);
export const rejectMyUserReportFix = (id, reason) =>
  api.post(`/user-reports/my/${id}/reject-fix`, { reason }).then(r => r.data);
export const adminListUserReports = (params = {}) =>
  api.get('/user-reports/admin/list', { params }).then(r => r.data);
export const adminUserReportDetail = (id) =>
  api.get(`/user-reports/admin/${id}`).then(r => r.data);
export const adminUpdateUserReport = (id, data) =>
  api.patch(`/user-reports/admin/${id}`, data).then(r => r.data);

// ===== DASHBOARD WIDGETS (Phase 5 Monday-style, 2026-05-16) =====
export const listDashboardWidgets = () =>
  api.get('/dashboard/widgets').then(r => r.data);
export const addDashboardWidget = (body) =>
  api.post('/dashboard/widgets', body).then(r => r.data);
export const updateDashboardWidget = (id, body) =>
  api.patch(`/dashboard/widgets/${id}`, body).then(r => r.data);
export const deleteDashboardWidget = (id) =>
  api.delete(`/dashboard/widgets/${id}`).then(r => r.data);
export const resetDashboardWidgets = () =>
  api.post('/dashboard/widgets/reset').then(r => r.data);
export const getWidgetData = (widgetType, params = {}) =>
  api.get(`/dashboard/widgets/data/${widgetType}`, { params }).then(r => r.data);

// ===== AUTOMATION ENGINE (Phase 3 Monday-style, 2026-05-16) =====
export const listAutomationRules = (params = {}) =>
  api.get('/automation/rules', { params }).then(r => r.data);
export const createAutomationRule = (body) =>
  api.post('/automation/rules', body).then(r => r.data);
export const updateAutomationRule = (id, body) =>
  api.patch(`/automation/rules/${id}`, body).then(r => r.data);
export const deleteAutomationRule = (id) =>
  api.delete(`/automation/rules/${id}`).then(r => r.data);
export const toggleAutomationRule = (id) =>
  api.post(`/automation/rules/${id}/toggle`).then(r => r.data);
export const seedPrebuiltAutomations = () =>
  api.post('/automation/seed-prebuilt').then(r => r.data);
export const listAutomationActions = () =>
  api.get('/automation/actions').then(r => r.data);
export const listAutomationEventTypes = () =>
  api.get('/automation/event-types').then(r => r.data);
export const testAutomationRule = (body) =>
  api.post('/automation/test', body).then(r => r.data);
export const listAutomationDrafts = (params = {}) =>
  api.get('/automation/drafts', { params }).then(r => r.data);

// ===== ACTIVITY FEED (Phase 1 Monday-style, 2026-05-16) =====
export const getItemActivity = (itemType, itemId, params = {}) =>
  api.get(
    `/activity/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`,
    { params },
  ).then(r => r.data);
export const addActivityNote = (itemType, itemId, text, mentions = []) =>
  api.post(
    `/activity/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}/note`,
    { text, mentions },
  ).then(r => r.data);
export const getRecentActivity = (params = {}) =>
  api.get('/activity/recent', { params }).then(r => r.data);
export const getActivityStats = (days_back = 7) =>
  api.get('/activity/stats', { params: { days_back } }).then(r => r.data);
export const listActivityEventTypes = () =>
  api.get('/activity/event-types').then(r => r.data);

// ===== PLANS & License (Etap 3, 2026-05-14) =====
export const getPlans = () => api.get('/plans').then(r => r.data);
export const getMyPlan = () => api.get('/plans/my-plan').then(r => r.data);
export const tenderConnections = (opp_id) =>
  api.get(`/intel/tenders/${encodeURIComponent(opp_id)}/connections`).then(r => r.data);

// Admin — Users & Tokens & Costs
export const adminListUsers = (params = {}) =>
  api.get('/admin/users', { params }).then(r => r.data);
export const adminSuspendUser = (user_id, reason) =>
  api.post(`/admin/users/${user_id}/suspend`, { reason }).then(r => r.data);
export const adminUnsuspendUser = (user_id) =>
  api.post(`/admin/users/${user_id}/unsuspend`).then(r => r.data);
export const adminDeactivateUser = (user_id) =>
  api.post(`/admin/users/${user_id}/deactivate`).then(r => r.data);
export const adminReactivateUser = (user_id) =>
  api.post(`/admin/users/${user_id}/reactivate`).then(r => r.data);
export const adminDeleteUser = (user_id, confirm_email) =>
  api.delete(`/admin/users/${user_id}`, { data: { confirm_email } }).then(r => r.data);
// ICP engine (T51/T52) — konfigurowalne pozyskiwanie B2B
export const icpGetSegments = () => api.get('/icp/segments').then(r => r.data);
export const icpGet = () => api.get('/icp').then(r => r.data);
export const icpSave = (body) => api.put('/icp', body).then(r => r.data);
export const icpGenerateQueries = () => api.post('/icp/generate-queries').then(r => r.data);
export const icpDiscover = (segment, country, max_pages = 3) =>
  api.post('/icp/discover', null, { params: { segment, country, max_pages } }).then(r => r.data);
export const icpExtractContacts = (limit = 50) =>
  api.post('/icp/extract-contacts', null, { params: { limit } }).then(r => r.data);
export const icpListLeads = (params = {}) => api.get('/icp/leads', { params }).then(r => r.data);

export const adminChangeRole = (user_id, role) =>
  api.patch(`/admin/users/${user_id}/role`, { role }).then(r => r.data);
export const adminChangeTenant = (user_id, tenant_id, role = null) =>
  api.patch(`/admin/users/${user_id}/tenant`, { tenant_id, role }).then(r => r.data);
export const adminAllocateTokens = (user_id, amount) =>
  api.post(`/admin/users/${user_id}/allocate-tokens`, { amount }).then(r => r.data);
export const adminTransferTokens = (from_user_id, to_user_id, amount) =>
  api.post('/admin/users/transfer-tokens', { from_user_id, to_user_id, amount }).then(r => r.data);
export const adminOrgTokenStatus = (tenant_id) =>
  api.get('/admin/org/token-status', { params: tenant_id ? { tenant_id } : {} }).then(r => r.data);
export const adminListTenants = () =>
  api.get('/admin/tenants').then(r => r.data);
export const adminScopeOptions = () =>
  api.get('/admin/scope-options').then(r => r.data);
export const adminSetTenantScope = (tenant_id, body) =>
  api.patch(`/admin/tenants/${tenant_id}/scope`, body).then(r => r.data);
export const adminUpdateTenantLicense = (tenant_id, data) =>
  api.patch(`/admin/tenants/${tenant_id}/license`, data).then(r => r.data);
export const adminCostBreakdown = (params = {}) =>
  api.get('/admin/costs/breakdown', { params }).then(r => r.data);
export const adminAuditLog = (limit = 100) =>
  api.get('/admin/audit-log', { params: { limit } }).then(r => r.data);
export const myUsage = () =>
  api.get('/admin/me/usage').then(r => r.data);

// BoQ Extraction
export const extractTenderBoq = (opp_id) =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/boq/extract`).then(r => r.data);
export const getTenderBoq = (opp_id) =>
  api.get(`/tenders/${encodeURIComponent(opp_id)}/boq`).then(r => r.data);

// Supplier Selection + RFQ Generation
export const selectSuppliersForTender = (opp_id, body = {}) =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/rfq/select-suppliers`, {
    suppliers_per_category: body.suppliers_per_category || 5,
    region_hint: body.region_hint,
  }).then(r => r.data);

export const generateTenderRfq = (opp_id, body = {}) =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/rfq/generate`, {
    operator_name: body.operator_name || 'Marek Kowalski',
    operator_role: body.operator_role || 'Specjalista ds. Zamówień Publicznych',
    operator_email: body.operator_email,
    operator_phone: body.operator_phone,
    response_deadline_days: body.response_deadline_days || 7,
    suppliers_per_category: body.suppliers_per_category || 3,
  }).then(r => r.data);

// RFQ Threads (operator workflow)
export const listRfqThreads = (params = {}) =>
  api.get('/rfq/threads', { params }).then(r => r.data);
export const getRfqThread = (thread_id) =>
  api.get(`/rfq/threads/${thread_id}`).then(r => r.data);
export const approveRfqThread = (thread_id, approved = true, notes = null) =>
  api.post(`/rfq/threads/${thread_id}/approve`, { approved, notes }).then(r => r.data);
export const sendRfqThread = (thread_id) =>
  api.post(`/rfq/threads/${thread_id}/send`).then(r => r.data);
export const getRfqThreadPdfUrl = (thread_id) => {
  const token = localStorage.getItem('token') || '';
  return `${api.defaults.baseURL}/rfq/threads/${thread_id}/pdf?token=${encodeURIComponent(token)}`;
};
export const getRfqThreadPdfBlob = async (thread_id) => {
  const r = await api.get(`/rfq/threads/${thread_id}/pdf`, { responseType: 'blob' });
  return URL.createObjectURL(r.data);
};
export const pollRfqInbox = (limit = 50) =>
  api.post('/rfq/inbox/poll', null, { params: { limit } }).then(r => r.data);

// Multi-Channel Orchestrator (Phases 9, 10, 11)
export const getChannelCapabilities = () =>
  api.get('/channels').then(r => r.data);
export const sendViaChannel = (thread_id, body) =>
  api.post(`/rfq/threads/${thread_id}/send-channel`, body).then(r => r.data);
export const planOutreach = (thread_id) =>
  api.post(`/rfq/threads/${thread_id}/plan-outreach`).then(r => r.data);
export const executeOutreachStep = (thread_id, step_num) =>
  api.post(`/rfq/threads/${thread_id}/execute-step`, { step_num }).then(r => r.data);

// Bid Optimizer + Cost Stack + Profitability
export const getTenderBidMix = (opp_id, days_to_deadline = null) => {
  const params = days_to_deadline ? { days_to_deadline } : {};
  return api.post(`/tenders/${encodeURIComponent(opp_id)}/bid-mix`, null, { params }).then(r => r.data);
};
export const getTenderCostStack = (opp_id, overrides = null) =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/cost-stack`, { overrides }).then(r => r.data);
export const getTenderProfitability = (opp_id, mode = 'auto') =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/profitability`, null,
    { params: { mode } }).then(r => r.data);

// Sprint 2 day 3 (2026-05-10): inline BoQ edit + bid_mode
export const patchBoqItem = (opp_id, item_id, body) =>
  api.patch(`/tenders/${encodeURIComponent(opp_id)}/boq/items/${item_id}`, body)
    .then(r => r.data);
export const setTenderBidMode = (opp_id, mode, reason = null) =>
  api.post(`/tenders/${encodeURIComponent(opp_id)}/bid-mode`,
    { mode, reason }).then(r => r.data);
export const suggestBidMode = (opp_id) =>
  api.get(`/tenders/${encodeURIComponent(opp_id)}/bid-mode/suggestion`).then(r => r.data);

export default api;
